import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import CabinetShell from './CabinetShell';
import LabShell from './LabShell';
import AdminLayout from '../../components/layout/AdminLayout';
import { AssignLabCard } from './AssignLabCard';
import { CertificateCard } from './CertificateCard';
import { ShipmentCard } from './ShipmentCard';
import { DocumentsCard } from './DocumentsCard';
import { OrderChat } from './OrderChat';
import { TimelineCard } from './TimelineCard';
import { statusMeta, fmtDate, fmtDateTime } from './orderConstants';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Info = ({ label, value }) => (
  <div>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="text-sm font-medium">{value || '—'}</p>
  </div>
);

const OrderContent = ({ orderId }) => {
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchAll = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/orders/${orderId}`);
      setOrder(data);
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error('Dossier introuvable');
    }
  }, [orderId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const doTransition = async (to) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/orders/${orderId}/transition`, { to_status: to });
      setOrder(data);
      toast.success(`Statut : ${statusMeta(to).label}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Transition impossible');
    } finally {
      setBusy(false);
    }
  };

  if (!order) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  const m = statusMeta(order.status);

  return (
    <div className="space-y-4" data-testid="order-detail">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-bold font-mono" data-testid="order-detail-number">{order.order_number}</h2>
          {order.urgent && <AlertTriangle className="h-5 w-5 text-orange-500" />}
          <Badge variant="outline" className={m.cls} data-testid="order-detail-status">{m.label}</Badge>
        </div>
        <div className="flex gap-2 flex-wrap">
          {(order.allowed_transitions || []).map((t) => (
            <Button key={t} size="sm" variant={t === 'cancelled' ? 'outline' : 'default'} disabled={busy}
              onClick={() => doTransition(t)} data-testid={`transition-${t}-btn`}>
              {statusMeta(t).label}
            </Button>
          ))}
        </div>
      </div>

      {user?.role === 'admin' && order.status === 'validated' && !order.fabricant_id && (
        <AssignLabCard orderId={orderId} onAssigned={(o) => { setOrder(o); fetchAll(); }} />
      )}

      {order.fabricant_id !== undefined || user?.role === 'lab' || ['sent_to_fabricant', 'in_production', 'fabricant_question', 'return_received', 'quality_control', 'shipped_to_cabinet', 'completed'].includes(order.status) ? (
        ['sent_to_fabricant', 'in_production', 'fabricant_question', 'return_received', 'quality_control', 'shipped_to_cabinet', 'completed'].includes(order.status)
          ? <CertificateCard order={order} onSubmitted={fetchAll} />
          : null
      ) : null}

      <ShipmentCard order={order} onChanged={fetchAll} />

      <Card className="border-border">
        <CardHeader><CardTitle className="text-base">Détails</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Info label="Cabinet" value={order.cabinet_name} />
          <Info label="Praticien" value={order.praticien_name} />
          <Info label="Patient" value={`${order.patient_last_name || ''} (${order.patient_ref})`} />
          <Info label="Type de travail" value={order.product_label} />
          <Info label="Dents" value={(order.teeth || []).join(', ')} />
          <Info label="Teinte" value={order.shade} />
          <Info label="Matériau" value={order.material} />
          <Info label="Scanner" value={order.scanner_used} />
          <Info label="Délai souhaité" value={fmtDate(order.deadline_requested)} />
          <Info label="Créé le" value={fmtDate(order.created_at)} />
          {order.fabricant_name && <Info label="Laboratoire" value={order.fabricant_name} />}
          {order.fabricant_deadline && <Info label="Deadline laboratoire" value={fmtDate(order.fabricant_deadline)} />}
        </CardContent>
        {order.comments && <CardContent className="pt-0 text-sm text-muted-foreground">{order.comments}</CardContent>}
      </Card>

      <DocumentsCard orderId={orderId} refreshKey={refreshKey} />

      <OrderChat order={order} onFileSent={() => setRefreshKey((k) => k + 1)} />

      <Card className="border-border">
        <CardHeader><CardTitle className="text-base">Historique des statuts</CardTitle></CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {[...(order.status_history || [])].reverse().map((h, i) => {
              const hm = statusMeta(h.status);
              return (
                <li key={i} className="flex items-center gap-3 text-sm">
                  <Badge variant="outline" className={hm.cls}>{hm.label}</Badge>
                  <span className="text-muted-foreground">{h.by_name} · {fmtDateTime(h.at)}</span>
                  {h.comment && <span className="text-muted-foreground italic">« {h.comment} »</span>}
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>

      {user?.role === 'admin' && <TimelineCard orderId={orderId} />}
    </div>
  );
};

export default function OrderDetail() {
  const { orderId } = useParams();
  const { user } = useAuth();
  if (user?.role === 'admin') {
    return <AdminLayout title="Dossier" subtitle="Détail de la commande"><OrderContent orderId={orderId} /></AdminLayout>;
  }
  if (user?.role === 'lab') {
    return <LabShell><OrderContent orderId={orderId} /></LabShell>;
  }
  return <CabinetShell><OrderContent orderId={orderId} /></CabinetShell>;
}
